import { MenuItem } from '@mui/material';
import { FC, useMemo } from 'react';
import { useQuery } from 'react-query';
import { ILot } from '../../models';
import { getNeighborhoodLots } from '../../fetch';
import { getCommonNeighborhoodLots } from '../../fetch/lookups';
import { SMBSelect } from '../input';

interface INeighborhoodLotsDropdownProps {
  value: string | number;
  handleChange: (val: string | number) => void;
  isDisabled?: boolean;
  setOptions?: (val: ILot[]) => void;
  apiParams?: any;
  isRequired?: boolean;
  inputLabel?: string;
  isEnabled?: boolean;
  shouldConvertArray?: boolean;
  isAdminPortal?: boolean;
}

export const NeighborhoodLotsDropdown: FC<INeighborhoodLotsDropdownProps> = ({
  inputLabel,
  isDisabled,
  isRequired,
  value,
  handleChange,
  setOptions,
  apiParams,
  isEnabled = true,
  shouldConvertArray = false,
  isAdminPortal = false
}) => {
  const arrayConvertedApiParams = useMemo(() => {
    if (shouldConvertArray && Array.isArray(apiParams?.neighborhoodId)) {
      // If neighborhoodId is an array and shouldConvertArray is true, join it into a comma-separated string
      return { ...apiParams, neighborhoodId: apiParams.neighborhoodId.join(',') };
    }
    // If neighborhoodId is not an array or shouldConvertArray is false, return the original apiParams
    return apiParams;
  }, [apiParams, shouldConvertArray]);

  const fetchLots = () =>
    isAdminPortal
      ? getNeighborhoodLots(arrayConvertedApiParams)
      : getCommonNeighborhoodLots(arrayConvertedApiParams);

  const { isLoading: isLoadingLots, data: lots } = useQuery<ILot[], Error>(
    ['getNeighborhoodLots', arrayConvertedApiParams, isAdminPortal],
    fetchLots,
    {
      notifyOnChangeProps: 'tracked',
      onSuccess: d => setOptions?.(d),
      enabled:
        isEnabled &&
        (!shouldConvertArray ||
          (Array.isArray(apiParams?.neighborhoodId) && apiParams.neighborhoodId.length === 1))
    }
  );

  return (
    <SMBSelect
      label={isLoadingLots ? 'Loading...' : inputLabel ?? 'Lots'}
      disabled={isDisabled || isLoadingLots}
      name="lots"
      handleClear={() => handleChange('')}
      id="lots"
      variant="outlined"
      value={value}
      required={isRequired}
      onChange={({ target: { value } }) => handleChange(Number(value))}
    >
      {!isLoadingLots && lots && lots.length === 0 && (
        <MenuItem disabled>No lots available</MenuItem>
      )}
      {!isLoadingLots &&
        lots &&
        lots.map((lot, index) => {
          return (
            <MenuItem key={`${index}`} value={lot.lotNumber}>
              {lot.description}
            </MenuItem>
          );
        })}
    </SMBSelect>
  );
};
